import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Loader2, ArrowLeft, Tag, Calendar, User } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ShareButton } from '@/components/ShareButton';
import { useLanguage } from '@/context/LanguageContext';
import { resolveMediaUrl } from '@/lib/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso, lang) => {
  try {
    return new Date(iso).toLocaleDateString(lang === 'hi' ? 'hi-IN' : 'en-IN', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch { return ''; }
};

export default function BlogDetailPage() {
  const { slug } = useParams();
  const { lang, pickLocale } = useLanguage();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/articles/${slug}`).then(({ data }) => {
      setArticle(data);
      // Related: same tag pool
      if (data.tags?.[0]) {
        axios.get(`${API}/articles?tag=${encodeURIComponent(data.tags[0])}&limit=4`).then(({ data: list }) => {
          setRelated((list || []).filter((a) => a.id !== data.id).slice(0, 3));
        });
      }
    }).catch(() => setArticle(null)).finally(() => setLoading(false));
  }, [slug]);

  const title = useMemo(() => article ? pickLocale(article, 'title', article.title_en) : '', [article, pickLocale]);
  const excerpt = useMemo(() => article ? pickLocale(article, 'excerpt', article.excerpt_en) : '', [article, pickLocale]);
  const body = useMemo(() => article ? pickLocale(article, 'body', article.body_en) : '', [article, pickLocale]);
  const metaTitle = useMemo(() => article ? (pickLocale(article, 'meta_title', article.meta_title_en) || title) : '', [article, pickLocale, title]);
  const metaDesc = useMemo(() => article ? (pickLocale(article, 'meta_description', article.meta_description_en) || excerpt) : '', [article, pickLocale, excerpt]);

  if (loading) {
    return <div className="pt-20 min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-orange" /></div>;
  }
  if (!article) {
    return (
      <div className="pt-20 min-h-screen flex flex-col items-center justify-center">
        <h1 className="font-heading text-2xl font-bold mb-4">Article Not Found</h1>
        <Link to="/blog"><Button variant="outline"><ArrowLeft className="h-4 w-4 mr-2" />Back to Blog</Button></Link>
      </div>
    );
  }

  const cover = article.cover_image ? resolveMediaUrl(article.cover_image) : null;

  // JSON-LD Article schema for SEO
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": metaDesc || excerpt,
    "image": cover || undefined,
    "datePublished": article.published_at || article.created_at,
    "dateModified": article.updated_at,
    "author": { "@type": "Organization", "name": article.author_name || "Shree Bajrang Developers" },
    "publisher": {
      "@type": "Organization",
      "name": "Shree Bajrang Developers",
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": typeof window !== 'undefined' ? window.location.href : '',
    },
    "keywords": (article.tags || []).join(', '),
  };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": typeof window !== 'undefined' ? window.location.origin : '' },
      { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${typeof window !== 'undefined' ? window.location.origin : ''}/blog` },
      { "@type": "ListItem", "position": 3, "name": title },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{`${metaTitle} | Shree Bajrang Developers`}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        {cover && <meta property="og:image" content={cover} />}
        <meta name="twitter:card" content="summary_large_image" />
        {article.canonical_url && <link rel="canonical" href={article.canonical_url} />}
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      </Helmet>

      <div className="pt-16 lg:pt-20" data-testid="blog-detail-page">
        {/* Breadcrumb */}
        <div className="bg-gray-50 border-b">
          <div className="max-w-4xl mx-auto px-4 lg:px-8 py-3 text-sm text-gray-600 flex items-center gap-2 overflow-x-auto whitespace-nowrap" data-testid="breadcrumb">
            <Link to="/" className="hover:text-brand-orange">Home</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-brand-orange">Blog</Link>
            <span>/</span>
            <span className="text-gray-900 font-medium truncate">{title}</span>
          </div>
        </div>

        {/* Hero */}
        <article className="max-w-4xl mx-auto px-4 lg:px-8 py-10 md:py-14">
          <div className="flex flex-wrap gap-2 mb-4">
            {(article.tags || []).map((tg) => (
              <Link key={tg} to={`/blog?tag=${encodeURIComponent(tg)}`}>
                <Badge variant="outline" className="hover:border-brand-orange hover:text-brand-orange"><Tag className="h-3 w-3 mr-1" />{tg}</Badge>
              </Link>
            ))}
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-gray-900 leading-tight" data-testid="blog-title">
            {title}
          </h1>
          {excerpt && (
            <p className="mt-4 text-lg text-gray-600 leading-relaxed">{excerpt}</p>
          )}

          <div className="mt-6 flex flex-wrap items-center justify-between gap-4 text-sm text-gray-500 border-y py-3">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="flex items-center gap-1.5"><User className="h-4 w-4" /> {article.author_name || 'Shree Bajrang'}</span>
              <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {formatDate(article.published_at || article.created_at, lang)}</span>
            </div>
            <ShareButton
              url={typeof window !== 'undefined' ? window.location.href : ''}
              title={title}
              description={excerpt}
            />
          </div>

          {cover && (
            <div className="mt-8 rounded-2xl overflow-hidden border aspect-[16/9] bg-gray-100">
              <img src={cover} alt={title} className="w-full h-full object-cover" />
            </div>
          )}

          <div
            className="prose prose-lg max-w-none mt-8 prose-headings:font-heading prose-a:text-brand-orange prose-img:rounded-xl prose-img:border"
            data-testid="blog-body"
            dangerouslySetInnerHTML={{ __html: body }}
          />
        </article>

        {related.length > 0 && (
          <section className="border-t bg-gray-50/60 py-12">
            <div className="max-w-7xl mx-auto px-4 lg:px-8">
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">{lang === 'hi' ? 'इन्हें भी पढ़ें' : 'You may also like'}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {related.map((a) => {
                  const rt = pickLocale(a, 'title', a.title_en);
                  return (
                    <Link key={a.id} to={`/blog/${a.slug}`} className="bg-white border rounded-xl overflow-hidden hover:shadow-md transition-shadow">
                      {a.cover_image && (
                        <div className="aspect-[16/9] overflow-hidden">
                          <img src={resolveMediaUrl(a.cover_image)} alt={rt} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="p-4">
                        <h3 className="font-heading font-semibold text-gray-900 line-clamp-2">{rt}</h3>
                        <p className="text-xs text-gray-500 mt-2">{formatDate(a.published_at, lang)}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}
